// PublicProfile Component - View another user's public profile

import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Star, Heart, Calendar, MapPin, Eye, EyeOff, Book, Clock, MessageCircle } from 'lucide-react';
import { PublicUserProfile, PlaceNote } from '../../types';
import { getUserProfile, getUserPublicReviews } from '../../services/userSearchService';
import { getPublicNotesForUser } from '../../services/notesService';
import { getMoodByValue } from '../../constants/moods';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Timestamp } from '@firebase/firestore';

interface PublicProfileProps {
  userId: string;
  onBack: () => void;
  onViewPlace?: (placeId: string) => void;
  onSendMessage?: (userId: string, userName: string, userPhoto: string | null) => void;
}

interface PublicReview {
  placeId: string;
  placeName: string;
  rating: number;
  comment: string;
}

export function PublicProfile({ userId, onBack, onViewPlace, onSendMessage }: PublicProfileProps) {
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [publicNotes, setPublicNotes] = useState<PlaceNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const userProfile = await getUserProfile(userId);

        if (!userProfile) {
          setError('Profil nicht gefunden');
          return;
        }

        if (!userProfile.privacy.searchable) {
          setError('Dieses Profil ist nicht oeffentlich');
          return;
        }

        setProfile(userProfile);

        // Load reviews if allowed
        if (userProfile.privacy.showReviews) {
          const userReviews = await getUserPublicReviews(userId);
          setReviews(userReviews);
        }

        // Load public logbook notes (notes user marked as public)
        try {
          const notes = await getPublicNotesForUser(userId);
          setPublicNotes(notes);
        } catch (notesErr) {
          // Silently fail - public notes are optional
          console.log('Could not load public notes:', notesErr);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Fehler beim Laden des Profils');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  const formatDate = (date: Timestamp | Date | undefined) => {
    if (!date) return '';
    const d = date instanceof Timestamp ? date.toDate() : date;
    return format(d, 'MMMM yyyy', { locale: de });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <EyeOff className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Profil nicht verfuegbar'}
          </h2>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600/90"
          >
            Zurueck
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Profil</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            {profile.photoURL ? (
              <img
                src={profile.photoURL}
                alt={profile.displayName}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="w-10 h-10 text-gray-400" />
              </div>
            )}

            {/* Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {profile.displayName}
              </h2>

              {profile.joinedAt && (
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mitglied seit {formatDate(profile.joinedAt)}
                </p>
              )}

              {profile.bio && (
                <p className="text-gray-600 mt-3">{profile.bio}</p>
              )}

              {/* Interests */}
              {profile.interests && profile.interests.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile.interests.map((interest, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-indigo-500/10 text-indigo-600 text-sm rounded-full"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              )}

              {/* Send Message Button */}
              {onSendMessage && profile.privacy.allowMessages !== 'none' && (
                <button
                  onClick={() => onSendMessage(userId, profile.displayName, profile.photoURL || null)}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Nachricht senden
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-6 mt-6 pt-6 border-t border-gray-100">
            {profile.privacy.showReviews && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-gray-900">
                  <Star className="w-5 h-5 text-yellow-400" />
                  {profile.reviewCount || 0}
                </div>
                <p className="text-sm text-gray-500">Bewertungen</p>
              </div>
            )}
            {profile.privacy.showFavorites && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-gray-900">
                  <Heart className="w-5 h-5 text-red-400" />
                  {profile.favoriteCount || 0}
                </div>
                <p className="text-sm text-gray-500">Favoriten</p>
              </div>
            )}
            {publicNotes.length > 0 && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-gray-900">
                  <Book className="w-5 h-5 text-indigo-500" />
                  {publicNotes.length}
                </div>
                <p className="text-sm text-gray-500">Logbuch</p>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        {profile.privacy.showReviews && reviews.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5" />
              Bewertungen
            </h3>

            <div className="space-y-4">
              {reviews.map((review, idx) => (
                <div
                  key={idx}
                  className="border-b border-gray-100 last:border-0 pb-4 last:pb-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      onClick={() => onViewPlace?.(review.placeId)}
                      className="font-medium text-gray-900 hover:text-indigo-600 transition-colors text-left flex items-center gap-1"
                    >
                      <MapPin className="w-4 h-4" />
                      {review.placeName}
                    </button>
                    {renderStars(review.rating)}
                  </div>
                  {review.comment && (
                    <p className="text-gray-600 mt-2 text-sm">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state for no reviews */}
        {profile.privacy.showReviews && reviews.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
            <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Noch keine Bewertungen</p>
          </div>
        )}

        {/* Public Logbook Section */}
        {publicNotes.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Book className="w-5 h-5 text-indigo-500" />
              Geteilte Logbuch-Einträge
              <span className="text-sm font-normal text-gray-500">
                ({publicNotes.length})
              </span>
            </h3>

            <div className="space-y-4">
              {publicNotes.map((note) => (
                <PublicNoteCard
                  key={note.id}
                  note={note}
                  onViewPlace={onViewPlace}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Public Note Card Component
interface PublicNoteCardProps {
  note: PlaceNote;
  onViewPlace?: (placeId: string) => void;
}

function PublicNoteCard({ note, onViewPlace }: PublicNoteCardProps) {
  const sharedDate = note.sharedAt instanceof Timestamp
    ? note.sharedAt.toDate()
    : note.sharedAt instanceof Date
      ? note.sharedAt
      : null;

  const visitDate = note.visitDate instanceof Timestamp
    ? note.visitDate.toDate()
    : note.visitDate instanceof Date
      ? note.visitDate
      : null;

  // Get moods
  const moods = note.moods?.map(m => getMoodByValue(m)).filter(Boolean) || [];
  if (moods.length === 0 && note.mood) {
    const legacyMood = getMoodByValue(note.mood);
    if (legacyMood) moods.push(legacyMood);
  }

  // Get first photo
  const photo = note.photoMetadata?.[0]?.thumbnailUrl || note.photoMetadata?.[0]?.url;

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors">
      <div className="flex">
        {/* Photo thumbnail */}
        {photo && (
          <div className="flex-shrink-0 w-24 h-24">
            <img
              src={photo}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-3 min-w-0">
          {/* Place name */}
          <button
            onClick={() => onViewPlace?.(note.placeId)}
            className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate">{note.placeName}</span>
          </button>

          {/* Note text */}
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{note.text}</p>

          {/* Moods */}
          {moods.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {moods.slice(0, 3).map((mood) => (
                <span
                  key={mood!.value}
                  className="inline-flex items-center gap-0.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full"
                >
                  <span>{mood!.emoji}</span>
                  <span>{mood!.label}</span>
                </span>
              ))}
              {moods.length > 3 && (
                <span className="text-xs text-gray-400">+{moods.length - 3}</span>
              )}
            </div>
          )}

          {/* Rating */}
          {note.personalRating && (
            <div className="flex items-center gap-0.5 mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < note.personalRating! ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer with dates */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
        {visitDate && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Besucht am {format(visitDate, 'd. MMM yyyy', { locale: de })}
          </span>
        )}
        {sharedDate && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Geteilt {formatDistanceToNow(sharedDate, { locale: de, addSuffix: true })}
          </span>
        )}
      </div>
    </div>
  );
}

export default PublicProfile;
